import { Globe, Sparkles } from 'lucide-react';
import { cn } from '@/utils/classNames';
import { useWebSearchToggle } from '@/hooks/useWebSearchToggle';
import type { Agent } from '@/types/agent.types';

export interface WebSearchToggleProps {
  agent: Agent | undefined;
}

const PILL_BASE =
  'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ' +
  'transition-colors focus:outline-none focus:ring-2 focus:ring-stellar-primary';

const PILL_ON = 'bg-stellar-primary text-white';
const PILL_OFF =
  'border border-border-default text-text-secondary hover:bg-bg-secondary';

export function WebSearchToggle({ agent }: WebSearchToggleProps) {
  const { state, setEnabled, setUseAdvanced, isVisible, advancedAvailable } =
    useWebSearchToggle(agent?.id, agent);

  if (!isVisible) return null;

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        aria-pressed={state.enabled}
        onClick={() => setEnabled(!state.enabled)}
        title="Search the web on the next prompts"
        className={cn(PILL_BASE, state.enabled ? PILL_ON : PILL_OFF)}
      >
        <Globe className="h-3.5 w-3.5" aria-hidden="true" />
        <span>Web</span>
      </button>

      {state.enabled && advancedAvailable && (
        <button
          type="button"
          aria-pressed={state.useAdvanced}
          onClick={() => setUseAdvanced(!state.useAdvanced)}
          title="Use Tavily advanced depth (slower, more thorough)"
          className={cn(PILL_BASE, state.useAdvanced ? PILL_ON : PILL_OFF)}
        >
          <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
          <span>Advanced</span>
        </button>
      )}
    </div>
  );
}
